import { useNavigate } from '@tanstack/react-router';
import { MessageSquareWarning } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/client';
import { useOpenCsReview } from '@/lib/api/cs-reviews';

export function ReviewOpenPage() {
  const navigate = useNavigate();
  const open = useOpenCsReview();
  const [conversation, setConversation] = useState('');
  const [complaint, setComplaint] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    open.mutate(
      { conversation: conversation.trim(), complaint: complaint.trim() || undefined },
      {
        onSuccess: (review) =>
          void navigate({
            to: '/review/$sessionId',
            params: { sessionId: review.sessionId },
          }),
        onError: (caught) =>
          setError(caught instanceof ApiError ? caught.message : 'Could not open the review.'),
      },
    );
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Review a draft</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Paste the Intercom conversation and say what the draft got wrong. The reviewer checks it
          with you and proposes the fix. Nothing changes until you confirm.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conversation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="conversation">Intercom link or id</Label>
            <Input
              id="conversation"
              placeholder="https://app.intercom.com/a/inbox/…/conversation/215475573164695"
              value={conversation}
              onChange={(event) => setConversation(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="complaint">What is wrong with the draft?</Label>
            <Textarea
              id="complaint"
              className="min-h-24"
              placeholder="It says FDA hold, but this order is not on Claudia's list."
              value={complaint}
              onChange={(event) => setComplaint(event.target.value)}
            />
          </div>
          {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
          <Button onClick={submit} disabled={open.isPending || !conversation.trim()}>
            <MessageSquareWarning className="size-4" />
            {open.isPending ? 'Opening…' : 'Start review'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
